import { useMutation, useQuery } from 'react-query'
import pickBy from 'lodash/pickBy'

import { useAuthedFetch } from '../../auth/useAuthedFetch'
import { useUserContext } from '../../auth/UserProvider'
import withFetchCancel from '../../libs/withFetchCancel'
import {
  transformServiceTypesResponse,
  transformInitialDataResponse,
  transformTransitRulesResponse,
} from './business'

const BACKEND_ENDPOINT = process.env.REACT_APP_BACKEND_ENDPOINT

//REVIEW: NOT USED
const useCentres = config => {
  const authedFetch = useAuthedFetch()

  const query = useQuery(
    'centres',
    () =>
      withFetchCancel(signal =>
        authedFetch('GET', `${BACKEND_ENDPOINT}/user/centres`, {
          parseJSON: true,
          signal,
        }),
      ),
    {
      select: ({ data }) => data,
      ...config,
    },
  )

  return query
}

const useTransitRules = (
  {
    clientCenterCode,
    originCountryCode,
    originPostalCode,
    destinCountryCode,
    destinPostalCode,
    serviceType,
    hasCustom,
  },
  config,
) => {
  const authedFetch = useAuthedFetch()
  const { webUserCode } = useUserContext()

  const selectedServiceType = serviceType?.code.split('-')[1] ?? undefined
  const selectedDestinCountryCode = destinCountryCode?.code ?? undefined

  const query = useQuery(
    ['transitRules', selectedServiceType],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'GET',
          `${BACKEND_ENDPOINT}/web-user-data/${webUserCode}/client_centers/${clientCenterCode}/transit_rules?origin_country_code=${originCountryCode}&destin_country_code=${selectedDestinCountryCode}&origin_postal_code=${originPostalCode}&destin_postal_code=${destinPostalCode}&shipping_type=${selectedServiceType}`,
          {
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      enabled: !!serviceType && hasCustom,
      select: ({ data }) => transformTransitRulesResponse(data),
      ...config,
    },
  )

  return query
}

const useServiceTypes = (
  {
    clientCenterCode,
    originCountryCode,
    originPostalCode,
    destinCountryCode,
    destinPostalCode,
    packages,
  },
  config,
) => {
  const authedFetch = useAuthedFetch()
  const { webUserCode } = useUserContext()

  const arePackagesValid = packages.every(pack => pack.weight > 0)

  const payload = {
    origin_country_code: originCountryCode,
    destin_country_code: destinCountryCode,
    origin_postal_code: originPostalCode,
    destin_postal_code: destinPostalCode,
    items: packages.map(pack => ({
      item_weight_declared: pack.weight ?? 0,
      item_width_declared: pack.width ?? 0,
      item_length_declared: pack.length ?? 0,
      item_height_declared: pack.height ?? 0,
    })),
    additionals: [],
  }

  const query = useQuery(
    [
      'serviceTypes',
      webUserCode,
      clientCenterCode,
      originCountryCode,
      originPostalCode,
      destinCountryCode,
      destinPostalCode,
    ],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/client_center_code/${clientCenterCode}/rpc-compute-available-shipping-types`,
          {
            data: payload,
            headers: {
              'Content-Type': 'application/json',
            },
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      select: ({ data }) => transformServiceTypesResponse(data),
      enabled:
        Boolean(clientCenterCode) &&
        Boolean(destinCountryCode) &&
        Boolean(originCountryCode) &&
        arePackagesValid,
      ...config,
      useErrorBoundary: false,
    },
  )

  return query
}

const useInitialData = (cognitoUserId, config) => {
  const authedFetch = useAuthedFetch()

  const query = useQuery(
    'initialData',
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'GET',
          `${BACKEND_ENDPOINT}/web-user-data/${cognitoUserId}`,
          {
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      select: ({ data }) => transformInitialDataResponse(data),
      ...config,
      // IMPORTANT This query is used before the error boundary is set, so we
      // need to disable that kind of error handling so that the application
      // doesn't end in an unwanted state.
      useErrorBoundary: false,
    },
  )

  return query
}

const useMobileServiceTypes = (
  {
    clientCenterCode,
    originCountryCode,
    originPostalCode,
    destinCountryCode,
    destinPostalCode,
    itemsCount = 0,
    shippingWeightDeclared = 0,
  },
  config,
) => {
  const authedFetch = useAuthedFetch()
  const { webUserCode } = useUserContext()

  const searchParams = {
    origin_country_code: originCountryCode,
    destin_country_code: destinCountryCode,
    origin_postal_code: originPostalCode,
    destin_postal_code: destinPostalCode,
    num_items: itemsCount,
    shipping_weight_declared: shippingWeightDeclared,
  }
  const truthySearchParams = pickBy(searchParams, val => Boolean(val))
  const qs = Boolean(Object.values(truthySearchParams).length)
    ? `available_shipping_types?${new URLSearchParams(
        truthySearchParams,
      ).toString()}`
    : ''

  const query = useQuery(
    [
      'serviceTypes',
      webUserCode,
      clientCenterCode,
      originCountryCode,
      originPostalCode,
      destinCountryCode,
      destinPostalCode,
    ],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'GET',
          `${BACKEND_ENDPOINT}/client_center_code/${clientCenterCode}/${qs}`,
          {
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      select: ({ data }) => transformServiceTypesResponse(data),
      enabled:
        Boolean(clientCenterCode) &&
        Boolean(destinCountryCode) &&
        Boolean(originCountryCode),
      ...config,
      useErrorBoundary: false,
    },
  )

  return query
}

const getStatisticsChartData = (data, statusList) => {
  const dataWithLabel = statusList.map(statusData => {
    return {
      ...statusData,
      value:
        data.find(datum => statusData?.code === datum.status_code)?.count ?? 0,
    }
  })

  const filteredDataTotalElems = dataWithLabel.reduce(
    (acc, shippingCodeData) => acc + shippingCodeData.value,
    0,
  )

  const filteredWithPercentage = dataWithLabel.map(shippingCodeData => ({
    ...shippingCodeData,
    percentage:
      Math.round((100 * shippingCodeData.value) / filteredDataTotalElems) ?? 0,
  }))

  return filteredWithPercentage
}

const useUserStatistics = (clientId, periodConfig, statusList) => {
  const authedFetch = useAuthedFetch()
  const { startDate, endDate } = periodConfig
  const encodedClientId = encodeURIComponent(clientId)
  const query = useQuery(
    ['userStatistics', { clientId, startDate, endDate }],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'GET',
          `${BACKEND_ENDPOINT}/client/${encodedClientId}/client_statistics?mapping_table_code=WEBCLIENT&shipping_date=${startDate}[range]${endDate}`,
          {
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      enabled: Boolean(clientId),
      select: ({ data }) => getStatisticsChartData(data.statistics, statusList),
    },
  )

  return query
}

const useUserCentersStatistics = (
  clientCentersId,
  periodConfig,
  statusList,
) => {
  const authedFetch = useAuthedFetch()
  const { startDate, endDate } = periodConfig
  const encodedClientCenters = encodeURIComponent(clientCentersId)
  const query = useQuery(
    ['userCentersStatistics', { encodedClientCenters, startDate, endDate }],
    () =>
      withFetchCancel(signal =>
        authedFetch(
          'GET',
          `${BACKEND_ENDPOINT}/client-centers/${encodedClientCenters}/client_statistics?mapping_table_code=WEBCLIENT&shipping_date=${startDate}[range]${endDate}`,
          {
            parseJSON: true,
            signal,
          },
        ),
      ),
    {
      enabled: Boolean(clientCentersId),
      select: ({ data }) => getStatisticsChartData(data.statistics, statusList),
    },
  )

  return query
}

const useUploadFiles = (clientId, centreCodeId) => {
  const authedFetch = useAuthedFetch()
  const encodedClientId = encodeURIComponent(clientId)
  const encodedCentreCodeId = encodeURIComponent(centreCodeId)

  return useMutation(
    async file => {
      const response = await withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/v1.0/${encodedClientId}/${encodedCentreCodeId}/shippings/electronic-documents/rpc-upload-file`,
          //`${BACKEND_ENDPOINT}/client/${encodedClientId}/shippings/electronic-documents/rpc-upload-file`,
          {
            parseJSON: true,
            signal,
            data: file,
          },
        ),
      )

      return response.data
    },
    {
      onSuccess: (data, variables) => {},
      useErrorBoundary: false,
    },
  )
}

export {
  useCentres,
  useTransitRules,
  useServiceTypes,
  useInitialData,
  useUserStatistics,
  useUserCentersStatistics,
  useUploadFiles,
  useMobileServiceTypes,
}
